import { useState, useEffect, type FormEvent } from 'react';
import toast from 'react-hot-toast';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import {
  sendReport,
  getMyReports,
  resolveReport,
  reopenReport,
  type Report,
} from '../../api/report.api';
import { getApiErrorMessage } from '../../utils/apiError';
import styles from './ReportPage.module.css';

const categories = [
  { value: 'connection', label: 'Ошибка подключения' },
  { value: 'speed', label: 'Низкая скорость' },
  { value: 'bug', label: 'Не работает сервис' },
  { value: 'other', label: 'Другое' },
];

const statusVariant = {
  new: 'info' as const,
  in_progress: 'warning' as const,
  resolved: 'success' as const,
  answered: 'warning' as const,
  reopened: 'info' as const,
};

const statusLabel: Record<string, string> = {
  new: 'Новое',
  in_progress: 'В работе',
  resolved: 'Решено',
  answered: 'Ответ получен',
  reopened: 'Возвращено',
};

export function ReportPage() {
  const [category, setCategory] = useState('connection');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [reports, setReports] = useState<Report[]>([]);
  const [reopenId, setReopenId] = useState<string | null>(null);
  const [reopenComment, setReopenComment] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    getMyReports().then(setReports).catch(() => {});
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (message.trim().length < 10) return;
    setSending(true);
    try {
      await sendReport(category, message);
      setSent(true);
      setMessage('');
      setTimeout(() => setSent(false), 3000);
      const updated = await getMyReports();
      setReports(updated);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Не удалось отправить обращение. Попробуйте позже'));
    } finally {
      setSending(false);
    }
  };

  const handleResolve = async (id: string) => {
    setActionLoading(true);
    try {
      await resolveReport(id);
      toast.success('Обращение закрыто');
      const updated = await getMyReports();
      setReports(updated);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Не удалось закрыть обращение'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleReopen = async (id: string) => {
    if (reopenComment.trim().length < 5) {
      toast.error('Комментарий слишком короткий (минимум 5 символов)');
      return;
    }
    setActionLoading(true);
    try {
      await reopenReport(id, reopenComment);
      toast.success('Обращение возвращено в работу');
      setReopenId(null);
      setReopenComment('');
      const updated = await getMyReports();
      setReports(updated);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Не удалось вернуть обращение'));
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' });

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Поддержка</h1>

      <form className={styles.form} onSubmit={handleSubmit}>
        <label className={styles.label}>Категория</label>
        <select className={styles.select} value={category} onChange={(e) => setCategory(e.target.value)}>
          {categories.map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>

        <label className={styles.label}>Описание проблемы</label>
        <textarea
          className={styles.textarea}
          placeholder="Опишите проблему подробно (минимум 10 символов)..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />

        {sent && <div className={styles.success}>Обращение отправлено!</div>}

        <Button type="submit" fullWidth loading={sending} disabled={message.trim().length < 10}>
          Отправить
        </Button>
      </form>

      {reports.length > 0 && (
        <>
          <h2 className={styles.historyTitle}>Мои обращения</h2>
          {reports.map((r) => (
            <div key={r.id} className={styles.reportItem}>
              <div className={styles.reportHeader}>
                <span className={styles.reportCategory}>
                  <span className={styles.reportNumber}>#{r.number}</span>
                  {categories.find((c) => c.value === r.category)?.label || r.category}
                </span>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <Badge variant={statusVariant[r.status as keyof typeof statusVariant] || 'neutral'}>
                    {statusLabel[r.status] || r.status}
                  </Badge>
                  <span className={styles.reportDate}>{formatDate(r.createdAt)}</span>
                </div>
              </div>
              <div className={styles.reportMessage}>{r.message}</div>

              {r.adminResponse && (
                <div className={styles.adminResponse}>
                  <div className={styles.adminResponseLabel}>Ответ поддержки:</div>
                  {r.adminResponse}
                </div>
              )}

              {r.userComment && r.status === 'reopened' && (
                <div className={styles.userComment}>
                  <div className={styles.userCommentLabel}>Ваш комментарий:</div>
                  {r.userComment}
                </div>
              )}

              {r.status === 'answered' && (
                <div className={styles.reportActions}>
                  <Button
                    variant="primary"
                    size="sm"
                    loading={actionLoading}
                    onClick={() => handleResolve(r.id)}
                  >
                    Решено
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      if (reopenId === r.id) {
                        setReopenId(null);
                        setReopenComment('');
                      } else {
                        setReopenId(r.id);
                        setReopenComment('');
                      }
                    }}
                  >
                    Вернуть в работу
                  </Button>
                </div>
              )}

              {reopenId === r.id && (
                <div className={styles.reopenForm}>
                  <textarea
                    className={styles.reopenTextarea}
                    placeholder="Опишите, что не помогло (минимум 5 символов)..."
                    value={reopenComment}
                    onChange={(e) => setReopenComment(e.target.value)}
                  />
                  <div className={styles.reopenActions}>
                    <Button
                      variant="primary"
                      size="sm"
                      loading={actionLoading}
                      disabled={reopenComment.trim().length < 5}
                      onClick={() => handleReopen(r.id)}
                    >
                      Отправить
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setReopenId(null);
                        setReopenComment('');
                      }}
                    >
                      Отмена
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </>
      )}
    </div>
  );
}
